import cuid from 'cuid';
import intl from 'react-intl-universal';
import * as APP_CONSTANTS from 'Constants/app.constant';
import { store } from 'Store/store.index';
import { getQueryVariable, getFromDriveNameForSharePoint, getFileNameWithoutExtension } from '@/utils/utils';
import { convertController, fileController } from '@/components/viewer/Controllers/index';
import { getFileBufferByUrl, DATA_TYPE } from 'Api/index';
import { convertService, thirdPartyService } from '@/store/services/index';
import { CONVERT_PDF_TO_WORD } from '@/components/viewer/ui/constants/convert.constants';
import { GA_FOR_SHAREPOINT } from '@/store/services/GoogleAnalyticsMap/SharePointGAMap';
import { USER_SIGN_IN_SUCCESS } from 'Store/constants';

const refreshTokenDuration = 50;

interface SharepointAndOneDriveFlowOptions {
  doCloseSidebar: Function;
  onShowCombineFilesDlg: Function;
  onShowMulFiles2PDFDlg: Function;
  onShowSaveAsDlg: Function;
  showNotActive: Function;
  showSubscribeForExpired: Function;
}

/**
 * SharepointAndOneDriveFlow is a function that handles the flow for SharePoint and OneDrive integration.
 *
 * @param {EventEmitter} emitter - The event emitter object.
 * @param {Object} options - The options object containing various callbacks and parameters.
 * @param {Function} options.doCloseSidebar - Callback function to close the sidebar.
 * @param {Function} options.onShowCombineFilesDlg - Callback function to show the combine files dialog.
 * @param {Function} options.onShowMulFiles2PDFDlg - Callback function to show the multiple files to PDF dialog.
 * @param {Function} options.onShowSaveAsDlg - Callback function to show the save as dialog.
 * @param {Function} options.showNotActive - Callback function to show notification for inactive action.
 * @param {Function} options.showSubscribeForExpired - Callback function to show subscription notification for expired action.
 * @returns {undefined}
 */
export async function SharepointAndOneDriveFlow (
  emitter: any,
  options: SharepointAndOneDriveFlowOptions,
): Promise<void> {
  let stateObj: any;
  const oneDriveState = getQueryVariable('oneDriveState');
  const accessTokenId = getQueryVariable('SharePointTokenId');
  const refreshTokenId = getQueryVariable('refreshTokenId');
  const error = getQueryVariable('error');
  const { doCloseSidebar, onShowCombineFilesDlg, onShowMulFiles2PDFDlg, onShowSaveAsDlg, /* showNotActive, showSubscribeForExpired */ } = options;
  
  if (error) {
    store.appStore.setGlobalMessage({ type: 'error', content: intl.get('SharePointLoginFailed') });

    return;
  }
  
  if (!oneDriveState || oneDriveState === 'undefined' || !accessTokenId || accessTokenId === 'undefined' || !refreshTokenId || refreshTokenId === 'undefined') {
    return;
  }

  const setRefreshTokenTimer = () => {
    // token的过期时间是一小时，50分钟的时候去刷新token
    let refreshTokenTimer = setTimeout(async () => {
      try {
        store.driveStore.sharePointRefreshTokenHandler(setRefreshTokenTimer);
      } catch (error) {
        console.error(error);
      }
    }, 60000 * refreshTokenDuration);

    store.driveStore.updateSharePointRefreshTokenTimer(refreshTokenTimer);
  };

  setRefreshTokenTimer();

  store.driveStore.updateFileFrom('officeFileHandler');
  store.appStore.updateSharePointInfo({ isFromSharePoint: true });
  store.docStore.setDocDriveName('SharePoint');

  try {
    const stateParam = JSON.parse(oneDriveState);

    stateObj = await store.driveStore.getFileHandlerState({ stateId: stateParam.stateId, refreshTokenId, accessTokenId });

    if (!stateObj.state) {
      store.driveStore.leaveSharePointEditMode();
      store.appStore.showLoading(false);
      store.appStore.setGlobalMessage({ type: 'error', content: intl.get('FileHandlerStateIdExpired') });

      return;
    }
  } catch (error) {
    throw error;
  }

  const state = JSON.parse(window.atob(stateObj.state));
  const { items } = state.activationParams;
  const { action } = state;
  const itemsLen = items.length;
  const refreshToken = stateObj.refreshToken;
  const accessToken = stateObj.accessToken;
  const token = accessToken;

  store.driveStore.updateSharepointToken(accessToken);
  store.driveStore.updateSharePointRefreshTokenInfo({ refresh_token: refreshToken });

  const doOpenFileFromOneDrive = async (successCallback?: Function) => {
    try {
      let driveItem: any = await store.driveStore.getFileFromFileHandler(items[0], token).catch(() => {
        throw 'get onedrive file failed.';
      });
      const fromDrive = getFromDriveNameForSharePoint(driveItem.webUrl);
      const backToSharePointUrl = driveItem.webUrl.substring(0, driveItem.webUrl.lastIndexOf('/'));
      store.driveStore.updateSharepointFilesInfoArr([driveItem]);
      store.appStore.updateSharePointInfo({ isFromSharePoint: true, backToSharePointUrl, action, fromDrive });

      if (driveItem.file.mimeType === 'application/pdf') {
        fileController.doOpenFileByUrl(driveItem['@microsoft.graph.downloadUrl'], driveItem.name, null, true, () => {
          successCallback && successCallback();
        });

        return;
      }

      await getFileBufferByUrl(driveItem['@microsoft.graph.downloadUrl'], (res) => {
        let fileObj = new File([res], driveItem.name, { type: driveItem.file.mimeType });

        convertController.doConvertToPDF(
          fileObj,
          async (result) => {
            if (result && result != 'stop' && result.ret == 0 && result.url) {
              const saveFileBackToSharePoint = async () => {
                const notificationId = cuid();

                try {
                  const { url, fileName } = result;

                  store.appStore.pushNotificationMessageArr(
                    Object.assign({}, APP_CONSTANTS.UploadingToSharePointNotification, { infoParam: { fromDrive }, notificationId }),
                  );

                  const fileBlob: any = await getFileBufferByUrl(url).catch(() => {
                    throw 'get file buffer error';
                  });
                  const fileNameWithoutExtension = getFileNameWithoutExtension(fileName);

                  fileBlob.name = `Convert-${fileNameWithoutExtension}-${cuid()}.pdf`;

                  // 将转换后的文档保存回SharePoint
                  const res = await store.driveStore.saveFileBackToDrive(fileBlob).catch(() => {
                    throw 'save combined file to drive error';
                  });

                  const { name } = res;

                  store.appStore.removeNotificationMessageArr(notificationId);
                  store.appStore.pushNotificationMessageArr(
                    Object.assign({}, APP_CONSTANTS.SavedToSharePointNotificationSucceed, { infoParam: { fromDrive } }),
                  );

                  fileController.doOpenFileByUrl(res['@content.downloadUrl'], name, null, true, () => {
                    const backToSharePointUrl = res.webUrl.substring(0, res.webUrl.lastIndexOf('/'));

                    store.driveStore.updateSharepointFilesInfoArr([res]);
                    store.appStore.updateSharePointInfo({ isFromSharePoint: true, backToSharePointUrl });
                    store.appStore.showLoading(false);
                    successCallback && successCallback();
                  });
                } catch (error) {
                  console.error(error);
                  store.appStore.showLoading(false);
                  store.appStore.pushNotificationMessageArr(
                    Object.assign({}, APP_CONSTANTS.SavedToSharePointNotificationFailed, { onErrorBtn: saveFileBackToSharePoint, infoParam: { fromDrive } }),
                  );
                  store.appStore.removeNotificationMessageArr(notificationId);
                }
              };

              await saveFileBackToSharePoint();
            }
          },
          () => {
            doCloseSidebar();
            store.appStore.showLoading(false);
          },
          true,
        );
      });
    } catch (error) {
      console.error(error);
    }
  };
  const doCombineFileFromOneDrive = async () => {
    let sharepointFilesInfoArr = [];
    let fileArr = [];

    try {
      for (let i = 0; i < items.length; i++) {
        const item = items[i];

        let driveItem: any = await store.driveStore.getFileFromFileHandler(item, token);
        let fileData: any = await convertService.doGetCloudDriveFileBuffer(
          { file: { driveName: 'OneDrive', item: driveItem } },
          DATA_TYPE.FileBuffer,
          cuid(),
          driveItem.lastModifiedDateTime,
        );

        sharepointFilesInfoArr.push(driveItem);
        fileArr.push(fileData);
      }

      store.driveStore.updateSharepointFilesInfoArr(sharepointFilesInfoArr);
      store.appStore.updateSharePointInfo({ action, fromDrive: getFromDriveNameForSharePoint(sharepointFilesInfoArr[0].webUrl) });
      store.appStore.updateOneDriveFileHandlerFileArr(fileArr);
      store.appStore.showLoading(false);
      onShowCombineFilesDlg({
        opener: "OneDriveFileHandler"
      });
    } catch (error) {
      console.log(error);
    }
  };
  const doConvertFileFromOneDrive = async () => {
    let sharepointFilesInfoArr = [];
    let fileArr = [];

    try {
      for (let i = 0; i < items.length; i++) {
        const item = items[i];

        let driveItem: any = await store.driveStore.getFileFromFileHandler(item, token);
        let fileData = await convertService.doGetCloudDriveFileBuffer(
          { file: { driveName: 'OneDrive', item: driveItem } },
          DATA_TYPE.File,
          cuid(),
          driveItem.lastModifiedDateTime,
        );

        sharepointFilesInfoArr.push(driveItem);
        fileArr.push(fileData);
      }

      store.driveStore.updateSharepointFilesInfoArr(sharepointFilesInfoArr);
      store.appStore.updateSharePointInfo({ action, fromDrive: getFromDriveNameForSharePoint(sharepointFilesInfoArr[0].webUrl) });
      store.appStore.updateOneDriveFileHandlerFileArr(fileArr);
      store.appStore.showLoading(false);
      onShowMulFiles2PDFDlg({
        opener: "OneDriveFileHandler"
      });
    } catch (error) {
      console.log(error);
    }
  };
  const openFile = async () => {
    store.appStore.showLoading(true);

    if ((action === 'open' || action === 'convert') && itemsLen === 1) {
      await doOpenFileFromOneDrive();
    } else if (action === 'combine') {
      await doCombineFileFromOneDrive();
    } else if (action === 'convert' && itemsLen > 1) {
      await doConvertFileFromOneDrive();
    } else if (action === 'export') {
      await doOpenFileFromOneDrive(() => {
        onShowSaveAsDlg(CONVERT_PDF_TO_WORD);
      });
    } else if (action === 'organize') {
      await doOpenFileFromOneDrive(async () => {
        try {
          const organizeTab = await window.pdfui.getComponentByName('organize-tab');

          organizeTab.active();
        } catch (error) {
          console.error(error);
        }
      });
    }

    const { fromDrive } = store.appStore.sharePointInfo;
    const gaParams = GA_FOR_SHAREPOINT[action][fromDrive];

    store.userStore.doSendGAEvent(gaParams);
  };

  if (action === 'open') {
    openFile()

    return 
  }

  const { hasSignIn } = store.userStore;
  const checkPermissionParams: any = {addInType: 'Sharepoint', hasPermissionCallback: openFile, action}

  if (hasSignIn) {
    const checkPassed = await thirdPartyService.checkPermissionAndReflect(checkPermissionParams)

    !checkPassed && store.appStore.showLoading(false)
  } else {
    store.userStore.loginIn(null, {isFromUserHandleLogin: true});
    emitter.on(USER_SIGN_IN_SUCCESS, async () => {
      const checkPassed = await thirdPartyService.checkPermissionAndReflect(checkPermissionParams)

      !checkPassed && store.appStore.showLoading(false)
    });
  }
}
